import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from 'date-fns';
import { useLanguage } from '@/contexts/LanguageContext';
import { CalendarIcon, Users, Clock } from 'lucide-react';

interface PasteOptionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (includeGuards: boolean) => void;
  targetDate: Date;
  shiftsCount: number;
  copiedWithGuards: boolean;
  copyMode: 'week' | 'custom' | 'month';
}

const PasteOptionsDialog: React.FC<PasteOptionsDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  targetDate,
  shiftsCount,
  copiedWithGuards,
  copyMode
}) => {
  const { t } = useLanguage();
  const [includeGuards, setIncludeGuards] = React.useState(copiedWithGuards);

  const handleConfirm = () => {
    onConfirm(includeGuards);
    onClose();
  };

  const formatTargetDate = () => {
    if (copyMode === 'month') {
      return format(targetDate, 'MMMM yyyy');
    }
    return format(targetDate, 'MMM d, yyyy');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            {t("pasteShifts") || "Paste Shifts"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">
                {t("pasteTarget") || "Paste to"}: {formatTargetDate()}
              </p>
              <p className="text-xs text-muted-foreground">
                {shiftsCount} {shiftsCount === 1 ? (t("shift") || "shift") : (t("shifts") || "shifts")}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-guards"
              checked={includeGuards}
              onCheckedChange={(checked) => setIncludeGuards(checked === true)}
            />
            <label
              htmlFor="include-guards"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              {t("includeAssignedGuards") || "Include assigned guards"}
            </label>
          </div>

          {copiedWithGuards && !includeGuards && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                {t("guardsWillBeRemoved") || "Guards will be removed from the pasted shifts"}
              </p>
            </div>
          )}

          {!copiedWithGuards && includeGuards && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                {t("noGuardsToPaste") || "Original shifts had no assigned guards"}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("cancel") || "Cancel"}
          </Button>
          <Button onClick={handleConfirm}>
            {t("pasteShifts") || "Paste Shifts"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PasteOptionsDialog;